import { useState } from "react"

import { useGetAssociatedTestTemplateList } from "../../TestTemplates/hooks"
import { PRODUCT_TEST_DOCUMENT_TYPES } from "../constants"
import { ProductTestActionDialogType } from "../types"

import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { AssociatedTestTemplate, ProductTest, ProductTestResult, SignedUrl } from "@/graphql/codegen/graphql"
import { AssociatedTestTemplateSortField, ReferenceTableName, SortingOrder } from "@/graphql/codegen/graphql"

interface UseProductTestActionsProps {
  productId: number
  onRefresh: () => void
}

interface UseProductTestActionsReturn {
  openedDialog: ProductTestActionDialogType | null
  associatedTestTemplates: AssociatedTestTemplate[]
  productTest: ProductTest | null
  productTestResult: ProductTestResult | null
  files: SignedUrl[]
  isFilesFetching: boolean
  refetchFiles: () => void
  handleFormDialogOpen: (productTest?: ProductTest) => void
  handleDeleteDialogOpen: (productTest: ProductTest) => void
  handleDocumentsDialogOpen: (productTest: ProductTest) => void
  handleOverrideDialogOpen: (productTest: ProductTest, productTestResult: ProductTestResult) => void
  handleDialogClose: () => void
  handleDialogSubmit: () => void
}

export const useProductTestActions = ({
  productId,
  onRefresh,
}: UseProductTestActionsProps): UseProductTestActionsReturn => {
  const [openedDialog, setOpenedDialog] = useState<ProductTestActionDialogType | null>(null)
  const [productTest, setProductTest] = useState<ProductTest | null>(null)
  const [productTestResult, setProductTestResult] = useState<ProductTestResult | null>(null)
  const { associatedTestTemplates } = useGetAssociatedTestTemplateList({
    productId,
    field: AssociatedTestTemplateSortField.SequenceNumber,
    order: SortingOrder.Asc,
  })
  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: productTest?.productTestId,
    fieldGroups: PRODUCT_TEST_DOCUMENT_TYPES,
    referenceTableName: ReferenceTableName.ProductTestDocument,
    pause: !productTest?.productTestId,
  })

  const handleFormDialogOpen = (productTest?: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.Form)
    setProductTest(productTest ?? null)
  }

  const handleDeleteDialogOpen = (productTest: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.DeleteConfirmation)
    setProductTest(productTest)
  }

  const handleDocumentsDialogOpen = (productTest: ProductTest) => {
    setOpenedDialog(ProductTestActionDialogType.Documents)
    setProductTest(productTest)
  }

  const handleOverrideDialogOpen = (productTest: ProductTest, productTestResult: ProductTestResult) => {
    setOpenedDialog(ProductTestActionDialogType.Override)
    setProductTest(productTest)
    setProductTestResult(productTestResult)
  }

  const handleDialogClose = () => {
    setOpenedDialog(null)
    setProductTest(null)
    setProductTestResult(null)
  }

  const handleDialogSubmit = () => {
    handleDialogClose()
    onRefresh()
  }

  return {
    openedDialog,
    associatedTestTemplates,
    productTest,
    productTestResult,
    files: fileList,
    isFilesFetching: fileFetching,
    refetchFiles: refetchFileUploadData,
    handleFormDialogOpen,
    handleDeleteDialogOpen,
    handleDocumentsDialogOpen,
    handleOverrideDialogOpen,
    handleDialogClose,
    handleDialogSubmit,
  }
}
